/**
 * WHAT SENT A VISITOR TO THE PACKAGE PICKER.
 *
 * Every in-app navigation to `/packages` names itself, and the picker records the name on its own view
 * event (`pages/packages.tsx`). Without that, picker exposure is one undifferentiated number: when it
 * moves there is no way to say which surface moved it, so a prompt tweak, a new upsell card and a real
 * product change all look identical.
 *
 * A closed union, not a string, so a new entry point cannot compile without naming itself.
 *
 * The `origin` param is a CONTRACT shared with the harness (`orion-harness-poc`
 * `src/credits/upgrade-url.ts`), which tags the links the assistant hands out — `credit_halt_stream`,
 * `credit_halt_async`, `skill_table`. Each side owns its own vocabulary; the picker reads the param
 * without caring which minted it, so the two never have to be kept in sync. Keep the values
 * snake_case and stable — dating a value's first appearance is how a jump gets pinned to a change.
 */
export type UpgradeOrigin =
  | 'sidebar_upgrade'
  | 'sidebar_topup'
  | 'settings_switch_plan'
  | 'settings_upgrade'
  | 'settings_get_more'
  | 'settings_topup'
  | 'chat_credits_card'
  | 'brief_upgrade'
  | 'personal_sale_banner'
  /** The Compliance page's Upgrade, offered when the account's plan carries no `hipaa` grant (T5.1). */
  | 'compliance_page';

/** The param the picker reads. Snake_case to match the BI dimension it lands in. */
export const UPGRADE_ORIGIN_PARAM = 'origin';
/** Set by the harness only — joins a picker view to the halt/link impression that caused it. */
export const UPGRADE_CONVERSATION_PARAM = 'conversation_id';

/**
 * Tag an existing picker href with its origin. Preserves whatever the caller already put on it (a
 * base path, the top-up deeplink's `topUpPackageId`) — parsed rather than string-appended so a second
 * `?` can't be produced.
 */
export function withUpgradeOrigin(href: string, origin: UpgradeOrigin, extra?: Record<string, string>): string {
  const [path, existing = ''] = href.split('?');
  const params = new URLSearchParams(existing);
  params.set(UPGRADE_ORIGIN_PARAM, origin);
  for (const [key, value] of Object.entries(extra ?? {})) {
    params.set(key, value);
  }
  return `${path}?${params.toString()}`;
}

/** The picker's own path, tagged. `extra` carries the top-up deeplink's params. */
export function upgradeHref(origin: UpgradeOrigin, extra?: Record<string, string>): string {
  return withUpgradeOrigin('/packages', origin, extra);
}

/** Every origin the WEB mints. Kept in step with the union above by `upgrade-origin.test.ts`. */
const WEB_ORIGINS: readonly string[] = [
  'sidebar_upgrade',
  'sidebar_topup',
  'settings_switch_plan',
  'settings_upgrade',
  'settings_get_more',
  'settings_topup',
  'chat_credits_card',
  'brief_upgrade',
  'personal_sale_banner',
  // The web Compliance page's Upgrade (T5.1). Without this row the picker's own view event reads a
  // Compliance-page open as UNATTRIBUTED — the exact blindness this union exists to remove.
  'compliance_page',
];

/**
 * Every origin the HARNESS mints (`orion-harness-poc` `src/credits/upgrade-url.ts` `UpgradeOrigin`).
 * Duplicated rather than imported: orion-web does not depend on the harness package, and this list is
 * a read-side allowlist, not the contract — a value missing here degrades to "unattributed", which is
 * the same thing an untagged open gets and is recoverable from the URL in the access log.
 */
const HARNESS_ORIGINS: readonly string[] = ['credit_halt_stream', 'credit_halt_async', 'skill_table'];

/**
 * Every origin the two NATIVE apps mint (`native-apps/ios/…/CreditsUpgrade.swift` `UpgradeOrigin` and
 * its Android twin). Platform-PREFIXED, unlike the web's own values, because both phones open this very
 * page in a browser and it cannot tell them apart from anything else it sees — the prefix is the only
 * thing that keeps an iOS drawer tap separable from an Android one.
 *
 * The suffixes deliberately mirror each other across the two platforms, so `…_settings_upgrade` groups
 * to one surface across all three clients when that is the question being asked.
 */
const NATIVE_SURFACES: readonly string[] = [
  'drawer_upgrade',
  'drawer_topup',
  'settings_upgrade',
  'settings_change_plan',
  'settings_topup',
  'chat_credits_card',
  'routine',
  'home_brief',
  'deeplink',
  'personal_sale_banner',
  // The phones' Compliance screen Upgrade (T5.1). Without this row `ios_compliance_page` /
  // `android_compliance_page` read as an untagged open on the picker's own view event, which is the
  // one thing `UpgradeOrigin` exists to prevent.
  'compliance_page',
];
const NATIVE_ORIGINS: readonly string[] = NATIVE_SURFACES.flatMap((surface) => [`ios_${surface}`, `android_${surface}`]);

const KNOWN_ORIGINS = new Set([...WEB_ORIGINS, ...HARNESS_ORIGINS, ...NATIVE_ORIGINS]);

/** The conversation id is a GUID; anything else did not come from us. */
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The origin (and conversation) a picker view was opened with.
 *
 * Read off `window.location` rather than `router.query` for the same reason the top-up deeplink is:
 * the query is empty on the first client render, and the view event fires on mount. Returns nothing
 * server-side and nothing for an untagged open — an untagged view is honestly unattributed, and
 * inventing a default would quietly file every one of them under whichever value we picked.
 *
 * BOTH VALUES ARE VALIDATED, because both are attacker-controlled: the picker is a public URL and
 * anyone can circulate one carrying whatever they like. An unbounded `origin` is unbounded cardinality
 * in a ClickHouse dimensions map, and an arbitrary `conversation_id` is a join key pointing at a row
 * that is not theirs. An unrecognised value is dropped to `undefined` — the same honest
 * "unattributed" an untagged open produces — never passed through and never substituted.
 */
export function readUpgradeAttribution(): { origin?: string; conversationId?: string } {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const params = new URL(window.location.href).searchParams;
    const origin = params.get(UPGRADE_ORIGIN_PARAM) ?? undefined;
    const conversationId = params.get(UPGRADE_CONVERSATION_PARAM) ?? undefined;
    return {
      origin: origin && KNOWN_ORIGINS.has(origin) ? origin : undefined,
      conversationId: conversationId && GUID.test(conversationId) ? conversationId : undefined,
    };
  } catch {
    return {};
  }
}

